import { EventEmitter } from '@angular/core';
import { LevelDesignerComponent } from '../level-designer/level-designer.component';
import { LevelDesignerElementBase } from './element-base';
import { ParkingRowElement } from './parking/parkingrow';
import { ParkingSpaceElement } from './parking/parkingspace';
import { ArrowElement } from './mapitems/shapes/arrow';
import { Striping } from './mapitems/shapes/striping';
import { FormControl, FormGroup, FormArray } from '@angular/forms';
import { PointElement } from './point-element';
import { PolygonElement } from './polygon-element';
import { Barrier } from "./devices/barrier";
import { Camera } from "./devices/camera";
import { CarCounter } from "./devices/car-counter";
import { Controller } from "./devices/controller";
import { Gateway } from "./devices/gateway";
import { GuidanceLight } from "./devices/guidance-light";
import { Sign } from "./devices/sign";
import { TrafficLight } from "./devices/traffic-light";
import { Gate } from './access-control/gate';
import { Lane } from './access-control/lane';
import { Wall } from './mapitems/objects/wall';
import { Stairs } from './mapitems/objects/stairs';
import { Elevator } from './mapitems/objects/elevator';
import { Ramp } from './mapitems/objects/ramp';
import { Pillar } from './mapitems/objects/pillar';
import { IslandElement } from './mapitems/objects/island';
import { Bush } from './mapitems/decorations/bush';
import { SmallTree } from './mapitems/decorations/small-tree';
import { TallTree } from './mapitems/decorations/tall-tree';
import { GrassIsland } from './mapitems/decorations/grass-island';
import { GardenBed } from './mapitems/decorations/garden-bed';
import { Business } from './mapitems/objects/business';
import { EmbossedRectangle } from '../utils/embossed-rectangle';
import { ParkingSpaceTypesService } from 'src/app/Services/parking-space-types.service';
import { MouseEventResult } from '../managers/manager-base';
import { ParkingMeter } from "./devices/parking-meter";

interface ToolboxSection {
  name: string;
  expanded: boolean;
  elements: ToolboxElement[];
}

interface ToolboxElement {
  name: string;
  constructor: () => LevelDesignerElementBase;
  icon?: string; // Path to the icon SVG
}

export class ElementToolbox {
  private ctx: CanvasRenderingContext2D;
  private width: number = 200;
  private height: number;
  private x: number = 0;
  private y: number = 60; // Below toolbar
  private sections: ToolboxSection[] = [];
  private isDragging: boolean = false;
  private levelDesigner: LevelDesignerComponent;
  public draggedElement: LevelDesignerElementBase | null = null;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private iconCache: Map<string, HTMLImageElement> = new Map();
  private scrollPosition: number = 0; // Track scroll position
  private totalContentHeight: number = 0; // Track total height of content
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private isCollapsed: boolean = false;
  private animationStartTime: number | null = null;
  private animationProgress: number = 1; // 0 to 1
  private headerHeight: number = 40; // Match toolbar height

  public onElementDragStart = new EventEmitter<LevelDesignerElementBase>();
  public onElementDragEnd = new EventEmitter<{element: LevelDesignerElementBase, x: number, y: number}>();

  constructor(
    levelDesigner: LevelDesignerComponent, 
    private canvas: HTMLCanvasElement,
    private parkingSpaceTypesService?: ParkingSpaceTypesService
  ) {
    this.ctx = canvas.getContext('2d')!;
    this.height = canvas.height - this.y;
    this.levelDesigner = levelDesigner;
    this.initializeSections();

    // Add wheel event listener
    //this.canvas.addEventListener('wheel', this.handleWheel.bind(this));
  }

  private initializeSections() {
    this.sections = [
      {
        name: 'Parking',
        expanded: true,
        elements: [
          {
            name: 'Space',
            constructor: () => new ParkingSpaceElement(new FormGroup({}), undefined, this.parkingSpaceTypesService),
            icon: 'assets/icons/parkingspace.svg'
          },
          {
            name: 'Row',
            constructor: () => new ParkingRowElement(new FormGroup({})),
            icon: 'assets/icons/parkingrow.svg'
          }
        ]
      },
      {
        name: 'Entry/Exit',
        expanded: true,
        elements: [
          {
            name: 'Gate',
            constructor: () => new Gate(new FormGroup({})),
            icon: 'assets/icons/gate.svg'
          },
          {
            name: 'Lane',
            constructor: () => new Lane(new FormGroup({})),
            icon: 'assets/icons/lane.svg'
          }
        ]
      },
      {
        name: 'Access Control',
        expanded: true,
        elements: [
          {
            name: 'Barrier',
            constructor: () => new Barrier(new FormGroup({})),
            icon: 'assets/icons/barrier-short.svg'
          },
          {
            name: 'Camera',
            constructor: () => new Camera(new FormGroup({})),
            icon: 'assets/icons/camera.svg'
          },
          {
            name: 'Controller',
            constructor: () => new Controller(new FormGroup({})),
            icon: 'assets/icons/controller.svg'
          },
          {
            name: 'Traffic Light',
            constructor: () => new TrafficLight(new FormGroup({})),
            icon: 'assets/icons/trafficlight.svg'
          },
          {
            name: 'Parking Meter',
            constructor: () => new ParkingMeter(new FormGroup({})),
            icon: 'assets/icons/parkingmeter.svg'
          }
        ]
      },
      {
        name: 'Guidance',
        expanded: true,
        elements: [
          {
            name: 'Car Counter',
            constructor: () => new CarCounter(new FormGroup({})),
            icon: 'assets/icons/carcounter.svg'
          },
          {
            name: 'Gateway',
            constructor: () => new Gateway(new FormGroup({})),
            icon: 'assets/icons/gateway.svg'
          },
          {
            name: 'Guidance Light',
            constructor: () => new GuidanceLight(new FormGroup({})),
            icon: 'assets/icons/guidancelight.svg'
          },
          {
            name: 'Sign',
            constructor: () => new Sign(new FormGroup({})),
            icon: 'assets/icons/sign.svg'
          }
        ]
      },
      {
        name: 'Markings',
        expanded: false,
        elements: [
          {
            name: 'Arrow',
            constructor: () => new ArrowElement(new FormGroup({})),
            icon: 'assets/icons/arrow.svg'
          },
          {
            name: 'Striping',
            constructor: () => new Striping(new FormGroup({})),
            icon: 'assets/icons/striping.svg'
          }
        ]
      },
      {
        name: 'Structural',
        expanded: false,
        elements: [
          {
            name: 'Island',
            constructor: () => new IslandElement(new FormGroup({})),
            icon: 'assets/icons/island.svg'
          },
          {
            name: 'Wall',
            constructor: () => new Wall(new FormGroup({})),
            icon: 'assets/icons/wall.svg'
          },
          {
            name: 'Stairs',
            constructor: () => new Stairs(new FormGroup({})),
            icon: 'assets/icons/stairs.svg'
          },
          {
            name: 'Elevator',
            constructor: () => new Elevator(new FormGroup({})),
            icon: 'assets/icons/elevator.svg'
          },
          {
            name: 'Ramp',
            constructor: () => new Ramp(new FormGroup({})),
            icon: 'assets/icons/ramp.svg'
          },
          {
            name: 'Pillar',
            constructor: () => new Pillar(new FormGroup({})),
            icon: 'assets/icons/pillar.svg'
          },
          {
            name: 'Business',
            constructor: () => new Business(new FormGroup({})),
            icon: 'assets/icons/business.svg'
          }
        ]
      },
      {
        name: 'Decorations',
        expanded: false,
        elements: [
          {
            name: 'Bush',
            constructor: () => new Bush(new FormGroup({})),
            icon: 'assets/icons/bush.svg'
          },
          {
            name: 'Small Tree',
            constructor: () => new SmallTree(new FormGroup({})),
            icon: 'assets/icons/smalltree.svg'
          },
          {
            name: 'Tall Tree',
            constructor: () => new TallTree(new FormGroup({})),
            icon: 'assets/icons/talltree.svg'
          },
          {
            name: 'Grass Island',
            constructor: () => new GrassIsland(new FormGroup({})),
            icon: 'assets/icons/grassisland.svg'
          },
          {
            name: 'Garden Bed',
            constructor: () => new GardenBed(new FormGroup({})),
            icon: 'assets/icons/gardenbed.svg'
          }
        ]
      }
      
    ];

    // Preload all icons
    this.sections.forEach(section => {
      section.elements.forEach(element => {
        if (element.icon && !this.iconCache.has(element.icon)) {
          this.loadIcon(element.icon);
        }
      });
    });
  }

  private loadIcon(path: string): void {
    if (this.iconCache.has(path)) return;

    const img = new Image();
    img.src = path;
    img.onload = () => {
      this.iconCache.set(path, img);
      this.levelDesigner.redraw();
    };
  }

  private drawIcon(icon: string | undefined, x: number, y: number, size: number): void {
    if (!icon) {
      // Draw a generic shape icon as fallback
      this.ctx.strokeStyle = '#666666';
      this.ctx.lineWidth = 1;
      this.ctx.strokeRect(x + 2, y + 2, size - 4, size - 4);
      return;
    }

    const img = this.iconCache.get(icon);
    if (img) {
      this.ctx.drawImage(img, x, y, size, size);
    } else {
      // Draw loading placeholder
      this.ctx.strokeStyle = '#cccccc';
      this.ctx.lineWidth = 1;
      this.ctx.strokeRect(x + 2, y + 2, size - 4, size - 4);
    }
  }

  public handleWheel(event: WheelEvent) {
    // Check if mouse is over toolbox
    const rect = this.canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    if (!this.isPointInside(mouseX, mouseY)) return;

    event.preventDefault(); // Prevent page scrolling

    // Calculate new scroll position
    const scrollSpeed = 30; // Adjust this value to control scroll speed
    this.scrollPosition += event.deltaY > 0 ? scrollSpeed : -scrollSpeed;

    // Clamp scroll position
    const maxScroll = Math.max(0, this.totalContentHeight - this.height);
    this.scrollPosition = Math.max(0, Math.min(this.scrollPosition, maxScroll));

    this.levelDesigner.redraw();
  }

  public draw() {
    // Calculate current height based on animation progress
    const fullHeight = this.canvas.height - this.y;
    const collapsedHeight = this.headerHeight;
    this.height = collapsedHeight + (fullHeight - collapsedHeight) * this.animationProgress;

    // Create gradient for header background (matching toolbar style)
    const gradient = this.ctx.createLinearGradient(this.x, this.y, this.x, this.y + this.headerHeight);
    gradient.addColorStop(0, '#E8E8E8');
    gradient.addColorStop(1, '#C8C8C8');

    // Draw header with gradient and embossed effect
    EmbossedRectangle.draw(this.ctx, this.x, this.y, this.width, this.headerHeight, {
        topLeft: 5,
        topRight: 5,
        bottomRight: this.isCollapsed ? 5 : 0,
        bottomLeft: this.isCollapsed ? 5 : 0
    }, gradient);

    // Draw header text
    this.ctx.fillStyle = '#333333';
    this.ctx.font = 'bold 12px Arial';
    this.ctx.textAlign = 'left';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('Elements', this.x + 10, this.y + this.headerHeight / 2);

    // Draw instruction text
    this.ctx.font = '10px Arial';
    this.ctx.fillStyle = '#666666';
    this.ctx.fillText('Drag elements to design surface', this.x + 10, this.y + this.headerHeight - 8);

    // Draw chevron
    const chevronX = this.x + this.width - 25;
    const chevronY = this.y + this.headerHeight / 2;
    const chevronSize = 8;
    
    this.ctx.beginPath();
    this.ctx.strokeStyle = '#666666';
    this.ctx.lineWidth = 2;
    
    if (this.isCollapsed) {
      // Draw chevron down
      this.ctx.moveTo(chevronX - chevronSize/2, chevronY - chevronSize/3);
      this.ctx.lineTo(chevronX, chevronY + chevronSize/3);
      this.ctx.lineTo(chevronX + chevronSize/2, chevronY - chevronSize/3);
    } else {
      // Draw chevron up
      this.ctx.moveTo(chevronX - chevronSize/2, chevronY + chevronSize/3);
      this.ctx.lineTo(chevronX, chevronY - chevronSize/3);
      this.ctx.lineTo(chevronX + chevronSize/2, chevronY + chevronSize/3);
    }
    this.ctx.stroke();

    // If collapsed or animating to collapsed, don't draw the rest
    if (this.animationProgress === 0) {
      return;
    }

    // Draw content with opacity based on animation progress
    this.ctx.globalAlpha = this.animationProgress;

    // Draw white background for content area
    this.ctx.fillStyle = '#ffffff';
    this.ctx.fillRect(this.x, this.y + this.headerHeight, this.width, this.height - this.headerHeight);

    let currentY = this.y + this.headerHeight + 10 - this.scrollPosition;
    this.ctx.textAlign = 'left';
    this.ctx.textBaseline = 'middle';
    
    // Draw sections
    this.sections.forEach(section => {
      // Only draw if section is visible
      if (currentY + 30 >= this.y && currentY <= this.y + this.height) {
        // Draw section header
        this.ctx.fillStyle = '#e0e0e0';
        this.ctx.fillRect(this.x + 5, currentY, this.width - 10, 30);
        
        // Draw expand/collapse arrow
        this.ctx.fillStyle = '#000000';
        this.ctx.font = '12px Arial';
        this.ctx.fillText(section.expanded ? '▼' : '▶', this.x + 10, currentY + 20);
        
        // Draw section name
        this.ctx.fillText(section.name, this.x + 30, currentY + 20);
      }
      
      currentY += 35;

      if (section.expanded) {
        section.elements.forEach(element => {
          // Only draw if element is visible
          if (currentY + 25 >= this.y && currentY <= this.y + this.height) {
            // Draw element item
            this.ctx.fillStyle = '#ffffff';
            const buttonX = this.x + 10;
            const buttonWidth = this.width - 20;
            this.ctx.fillRect(buttonX, currentY, buttonWidth, 25);
            
            // Draw icon
            const iconSize = 20;
            const iconX = buttonX + 5;
            const iconY = currentY + 2;
            this.drawIcon(element.icon, iconX, iconY, iconSize);
            
            // Draw element name
            this.ctx.fillStyle = '#000000';
            this.ctx.fillText(element.name, buttonX + iconSize + 10, currentY + 13);
          }
          
          currentY += 30;
        });
      }
    });

    // Update total content height
    this.totalContentHeight = currentY + this.scrollPosition - this.y;

    // Draw scroll indicators if content is scrollable
    if (this.totalContentHeight > this.height) {
      const indicatorWidth = 4;
      const scrollbarHeight = (this.height / this.totalContentHeight) * this.height;
      const scrollbarY = this.y + (this.scrollPosition / this.totalContentHeight) * this.height;
      
      // Draw scroll track
      this.ctx.fillStyle = '#e0e0e0';
      this.ctx.fillRect(this.x + this.width - indicatorWidth - 2, this.y, indicatorWidth, this.height);
      
      // Draw scroll thumb
      this.ctx.fillStyle = '#999999';
      this.ctx.fillRect(this.x + this.width - indicatorWidth - 2, scrollbarY, indicatorWidth, scrollbarHeight);
    }

    this.ctx.globalAlpha = 1;
  }

  public handleMouseDown(event: MouseEvent): boolean {
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    if (!this.isPointInside(x, y)) return false;

    // Check if click is in the header area
    if (y >= this.y && y <= this.y + this.headerHeight) {
      // Check if click is on the chevron
      const chevronX = this.x + this.width - 25;
      const chevronY = this.y + this.headerHeight / 2;
      const chevronSize = 8;
      
      if (Math.abs(x - chevronX) <= chevronSize && Math.abs(y - chevronY) <= chevronSize) {
        this.toggleCollapse();
        return true;
      }
    }

    // If collapsed, don't handle any other clicks
    if (this.isCollapsed || this.animationProgress === 0) {
      return true;
    }

    let currentY = this.y + this.headerHeight + 10 - this.scrollPosition;

    // Check section headers
    for (const section of this.sections) {
      if (y >= currentY && y <= currentY + 30 && 
          x >= this.x + 5 && x <= this.x + this.width - 5) {
        section.expanded = !section.expanded;
        this.levelDesigner.redraw();
        return true;
      }
      
      currentY += 35;

      if (section.expanded) {
        for (const element of section.elements) {
          if (y >= currentY && y <= currentY + 25 &&
              x >= this.x + 10 && x <= this.x + this.width - 10) {
            // Start dragging
            this.isDragging = true;
            this.dragStartX = x;
            this.dragStartY = y;
            
            // Create new element instance and position it at cursor
            this.draggedElement = element.constructor().default();
            
            // Convert screen coordinates to world coordinates
            const worldX = this.levelDesigner.getWorldCoordinates(event).x;
            const worldY = this.levelDesigner.getWorldCoordinates(event).y;

            // Position the element at cursor
            if (this.draggedElement instanceof PointElement) {
                this.draggedElement.setLevelDesigner(this.levelDesigner);
                this.draggedElement.moveTo(worldX, worldY);
            } else if (this.draggedElement instanceof PolygonElement) {
                const points = this.draggedElement.getPoints();
                const centerX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
                const centerY = points.reduce((sum, p) => sum + p.y, 0) / points.length;
                
                points.forEach(point => {
                    point.x += worldX - centerX;
                    point.y += worldY - centerY;
                });
            }

            this.onElementDragStart.emit(this.draggedElement);
            
            // Reset last mouse position when starting drag
            this.lastMouseX = worldX;
            this.lastMouseY = worldY;
            
            return true;
          }
          currentY += 30;
        }
      }
    }

    return false;
  }

  public handleMouseMove(event: MouseEvent) {
    if(this.draggedElement === null) return false;
    const rect = this.canvas.getBoundingClientRect();
    const mouseX = this.levelDesigner.getWorldCoordinates(event).x;
    const mouseY = this.levelDesigner.getWorldCoordinates(event).y;

    if (!this.isDragging || !this.draggedElement) return false;

    if (this.draggedElement instanceof PointElement) {
        this.draggedElement.moveTo(mouseX, mouseY);
    } else {
        // Calculate the delta movement since last position
        const deltaX = mouseX - this.lastMouseX;
        const deltaY = mouseY - this.lastMouseY;

        if (this.lastMouseX !== 0) {
            this.draggedElement.move(deltaX, deltaY);
        }
    }

    this.lastMouseX = mouseX;
    this.lastMouseY = mouseY;
    
    return true;
  }

  public handleMouseUp(event: MouseEvent) {
    if (!this.isDragging || !this.draggedElement) return false;

    const validationResult = this.draggedElement.checkPositionValid(this.levelDesigner.elements);
    if (validationResult.errors.length > 0) {
        // Show validation errors to the user
        validationResult.errors.forEach(error => {
            this.levelDesigner.toastService.error(error, 'Placement Error');
        });
        
        this.isDragging = false;
        this.draggedElement = null;
        this.levelDesigner.redraw();
        return false;
    }
    
    // Add element to the form based on its ParentFormGroupName
    if (this.levelDesigner.levelData && this.draggedElement.ParentFormGroupName) {
        // Get or create the parent form array
        let parentFormArray = this.draggedElement instanceof ParkingSpaceElement ? 
            this.draggedElement.currentRow?.formGroup.get(this.draggedElement.ParentFormGroupName) as FormArray : 
            this.levelDesigner.levelData.get(this.draggedElement.ParentFormGroupName) as FormArray;
        
        if (!parentFormArray) {
            //no parent form array so 
            parentFormArray = new FormArray<any>([]);
            this.levelDesigner.levelData.addControl(this.draggedElement.ParentFormGroupName, parentFormArray);
        }

        // Special handling for nested form arrays (like Spaces in Rows)
        if (this.draggedElement.ParentType && validationResult.parentElement) {
            // Add to the appropriate form array in the parent's form group
            const nestedFormArray = validationResult.parentElement.formGroup.get(this.draggedElement.ParentFormGroupName) as FormArray;
            if (nestedFormArray) {
                // Only add if not already present
                if (!nestedFormArray.controls.includes(this.draggedElement.formGroup)) 
                  nestedFormArray.push(this.draggedElement.formGroup);
                // Mark both the nested form array and parent form group as dirty
                nestedFormArray.markAsDirty();
                validationResult.parentElement.formGroup.markAsDirty();
            }
        } else {
            // Top-level elements go directly in their form array
            if (!parentFormArray.controls.includes(this.draggedElement.formGroup)) 
            parentFormArray.push(this.draggedElement.formGroup);
            parentFormArray.markAsDirty();
        }

        this.draggedElement.updateForm();

        this.levelDesigner.addElement(this.draggedElement);
        //this.levelDesigner.elements?.push(this.draggedElement);
        //this.levelDesigner.sortElementsByLayer();
        //this.levelDesigner.saveState();

        //this.levelDesigner.levelData.markAsDirty();
        //this.levelDesigner.levelData.markAsTouched();
    }
   
    this.isDragging = false;
    this.draggedElement = null;
    this.levelDesigner.redraw();

    // Reset last mouse position when ending drag
    this.lastMouseX = 0;
    this.lastMouseY = 0;

    return true;
  }

  public isPointInside(x: number, y: number): boolean {
    return x >= this.x && x <= this.x + this.width &&
           y >= this.y && y <= this.y + this.height;
  }

  public Refresh() {
    this.draw();
  }

  public createElement(type: string, formGroup: FormGroup) : LevelDesignerElementBase | null {
    
    // Check if type exists in level designer's element constructors
    const constructor = this.levelDesigner.elementConstructors.get(type.toLowerCase());
    if (constructor) {
      return constructor(formGroup).default();
    }
    return null;
  }

  private handleAnimation(currentTime: number) {
    if (this.animationStartTime === null) {
      this.animationStartTime = currentTime;
    }

    const elapsed = currentTime - this.animationStartTime;
    const duration = 500; // 500ms animation duration
    
    if (elapsed < duration) {
      this.animationProgress = this.isCollapsed ? 
        1 - (elapsed / duration) : // Collapsing
        elapsed / duration;        // Expanding
      
      // Use easeInOutCubic for smooth animation
      const t = this.animationProgress;
      this.animationProgress = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      
      requestAnimationFrame(this.handleAnimation.bind(this));
    } else {
      this.animationProgress = this.isCollapsed ? 0 : 1;
      this.animationStartTime = null;
    }
    
    this.levelDesigner.redraw();
  }

  private toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
    this.animationStartTime = null;
    requestAnimationFrame(this.handleAnimation.bind(this));
  }

  // Add these methods for position and dimension control
  public setPosition(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  public setDimensions(width: number, height: number) {
    this.width = width;
    this.headerHeight = height;
  }

  public handleDoubleClick(x: number, y: number): MouseEventResult | undefined {
    // Check if click is inside the toolbox area
    if (this.isPointInside(x, y)) {
      // Double click inside toolbox - do nothing by default
      return { cursor: 'default', stopPropagation: true, redraw: false };
    }
    return undefined;
  }
}
